import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Row,
  Select,
  TextArea,
  TextField,
} from '@rematter/ui-web';
import {
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogOverlay,
  DialogMain,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { FreightLaneCostHistory } from './FreightLaneCostHistory';

import { AccountSelect } from '~/modules/transportation/components/@shared/AccountSelect';
import { LocationPickerField } from '~/modules/transportation/components/@shared/LocationPickerField';
import { useCreateFreightLaneCostMutation } from '~/modules/transportation/hooks/mutations/useCreateFreightLaneCostMutation';
import { useCreateFreightLaneMutation } from '~/modules/transportation/hooks/mutations/useCreateFreightLaneMutation';
import { useCreateFreightLaneRouteMutation } from '~/modules/transportation/hooks/mutations/useCreateFreightLaneRouteMutation';
import { useDeleteFreightLaneMutation } from '~/modules/transportation/hooks/mutations/useDeleteFreightLaneMutation';
import { useUpdateFreightLaneMutation } from '~/modules/transportation/hooks/mutations/useUpdateFreightLaneMutation';
import {
  $freightLaneForm,
  $resetFreightLaneForm,
  $updateFreightLaneForm,
} from '~/modules/transportation/state/freightLaneForm.localState';
import { FreightLaneRecord } from '~/modules/transportation/types';

type FreightLaneDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  freightLane?: FreightLaneRecord;
};

export const FreightLaneDialog = ({
  isOpen,
  onClose,
  onSuccess,
  freightLane,
}: FreightLaneDialogProps) => {
  const t = useTranslations();
  const [isEditMode, setIsEditMode] = React.useState(false);
  const form = useAtomValue($freightLaneForm);
  const resetForm = useSetAtom($resetFreightLaneForm);
  const updateForm = useSetAtom($updateFreightLaneForm);
  const toast = useToast();

  const isCreating = !freightLane;

  const { mutateAsync: createFreightLane, isPending: isCreatePending } =
    useCreateFreightLaneMutation();
  const { mutateAsync: updateFreightLane, isPending: isUpdatePending } =
    useUpdateFreightLaneMutation();
  const { mutateAsync: deleteFreightLane, isPending: isDeletePending } =
    useDeleteFreightLaneMutation();
  const { mutateAsync: createFreightLaneRoute } =
    useCreateFreightLaneRouteMutation();
  const { mutateAsync: createFreightLaneCost } =
    useCreateFreightLaneCostMutation();

  const [costStartDate, setCostStartDate] = React.useState<CalendarDate | null>(
    null,
  );
  const [costEndDate, setCostEndDate] = React.useState<CalendarDate | null>(
    null,
  );
  const [costRateFormula, setCostRateFormula] = React.useState('');
  const [costChargebackFormula, setCostChargebackFormula] = React.useState('');
  const [costFuelSurchargeFormula, setCostFuelSurchargeFormula] =
    React.useState('');
  const [costFuelSurchargeMode, setCostFuelSurchargeMode] = React.useState('');
  const [costMinimumCharge, setCostMinimumCharge] = React.useState('');
  const [costCurrency, setCostCurrency] = React.useState('USD');

  const isPending = isCreatePending || isUpdatePending || isDeletePending;

  React.useEffect(() => {
    if (freightLane) {
      updateForm({
        id: freightLane.id,
        carrierId: freightLane.carrierId,
        transportationMode: 'TRUCK',
        originLocationId: freightLane.originLocationId ?? '',
        originLocationType: freightLane.originLocationType ?? '',
        freightLaneRouteId: freightLane.freightLaneRouteId ?? '',
        routeLocationAId: freightLane.routeLocationAId ?? '',
        routeLocationBId: freightLane.routeLocationBId ?? '',
        destinationCity: freightLane.destinationCity ?? '',
        destinationState: freightLane.destinationState ?? '',
        destinationZipCode: freightLane.destinationZipCode ?? '',
        destinationCountry: freightLane.destinationCountry ?? '',
        active: freightLane.active,
        notes: freightLane.notes ?? '',
      });
      setIsEditMode(false);
    } else {
      resetForm();
      setIsEditMode(true);
      setCostStartDate(null);
      setCostEndDate(null);
      setCostRateFormula('');
      setCostChargebackFormula('');
      setCostFuelSurchargeFormula('');
      setCostFuelSurchargeMode('');
      setCostMinimumCharge('');
      setCostCurrency('USD');
    }
  }, [freightLane, updateForm, resetForm]);

  const handleClose = () => {
    onClose();
    setIsEditMode(false);
    resetForm();
  };

  const resolveRouteId = async (): Promise<string | undefined> => {
    if (form.routeLocationAId && form.routeLocationBId) {
      if (form.freightLaneRouteId) return form.freightLaneRouteId;
      const result = await createFreightLaneRoute({
        locationAId: form.routeLocationAId,
        locationAType: (form.routeLocationAType || 'Facility') as
          | 'Facility'
          | 'AccountAddress',
        locationBId: form.routeLocationBId,
        locationBType: (form.routeLocationBType || 'Facility') as
          | 'Facility'
          | 'AccountAddress',
      });
      return result?.transportationFreightLaneRouteCreate?.id;
    }
    return undefined;
  };

  const handleSave = async () => {
    if (!form.carrierId) return;

    try {
      const routeId = await resolveRouteId();

      if (isCreating) {
        const laneResult = await createFreightLane({
          carrierId: form.carrierId,
          transportationMode: 'TRUCK',
          active: form.active,
          originLocationId: form.originLocationId || undefined,
          originLocationType: form.originLocationType || undefined,
          destinationCity: form.destinationCity || undefined,
          destinationState: form.destinationState || undefined,
          destinationZipCode: form.destinationZipCode || undefined,
          destinationCountry: form.destinationCountry || undefined,
          notes: form.notes || undefined,
          freightLaneRouteId: routeId,
        });

        const newLaneId = laneResult?.transportationFreightLaneCreate?.id;
        if (newLaneId && costStartDate && costRateFormula) {
          await createFreightLaneCost({
            freightLaneId: newLaneId,
            startDate: costStartDate.toString(),
            endDate: costEndDate?.toString() || undefined,
            rateFormula: costRateFormula,
            chargebackFormula: costChargebackFormula || undefined,
            fuelSurchargeFormula: costFuelSurchargeFormula || undefined,
            fuelSurchargeMode: costFuelSurchargeMode || undefined,
            minimumChargeAmount: costMinimumCharge || undefined,
            currency: costCurrency,
          });
        }
        toast.success(t.freightLanes.messages.createSuccess);
      } else {
        await updateFreightLane({
          id: freightLane.id,
          carrierId: form.carrierId,
          transportationMode: 'TRUCK',
          active: form.active,
          originLocationId: form.originLocationId || null,
          originLocationType: form.originLocationType || null,
          destinationCity: form.destinationCity || null,
          destinationState: form.destinationState || null,
          destinationZipCode: form.destinationZipCode || null,
          destinationCountry: form.destinationCountry || null,
          notes: form.notes || null,
          freightLaneRouteId: routeId ?? undefined,
        });
        toast.success(t.freightLanes.messages.updateSuccess);
      }
      setIsEditMode(false);
      onSuccess();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t.freightLanes.messages.error,
      );
    }
  };

  const handleDelete = async () => {
    if (!freightLane) return;

    try {
      await deleteFreightLane({ id: freightLane.id });
      toast.success(t.freightLanes.messages.deleteSuccess);
      onSuccess();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t.freightLanes.messages.error,
      );
    }
  };

  const isFormDisabled = !isEditMode && !isCreating;

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={handleClose}
      isDismissable={false}
      className="@FreightLaneDialog"
    >
      <DialogContent position="right">
        <DialogHeader slot="title">
          <div className="text-xl font-semibold">
            {isCreating
              ? t.freightLanes.dialog.title.create
              : isEditMode
                ? t.freightLanes.dialog.title.edit
                : t.freightLanes.dialog.title.view}
          </div>
        </DialogHeader>

        <DialogMain>
          <Col gap4>
            <Select
              label={t.freightLanes.dialog.fields.transportationMode}
              value={form.transportationMode || 'TRUCK'}
              onChange={(value) =>
                updateForm({ transportationMode: value ?? 'TRUCK' })
              }
              options={[{ key: 'TRUCK', label: 'Truck' }]}
              isRequired
              isDisabled={isFormDisabled}
            />

            <AccountSelect
              label={t.freightLanes.dialog.fields.carrier}
              value={form.carrierId || null}
              onChange={(account) => {
                updateForm({
                  carrierId: account?.id ?? '',
                });
              }}
              isRequired
              isDisabled={isFormDisabled}
              isCarrier
            />

            <div className="border-t pt-4">
              <Col gap4>
                <div className="text-sm font-medium text-neutral/70">
                  Route (Origin → Destination)
                </div>
                <LocationPickerField
                  label="Origin"
                  value={form.routeLocationAId || null}
                  onChange={(locationId, _name, locationType) => {
                    updateForm({
                      routeLocationAId: locationId ?? '',
                      routeLocationAType: locationType ?? '',
                      freightLaneRouteId: '',
                    });
                    if (locationId && form.routeLocationBId) {
                      updateForm({
                        destinationCity: '',
                        destinationState: '',
                        destinationZipCode: '',
                        destinationCountry: '',
                        originLocationId: '',
                        originLocationType: '',
                      });
                    }
                  }}
                  isDisabled={isFormDisabled}
                />
                <LocationPickerField
                  label="Destination"
                  value={form.routeLocationBId || null}
                  onChange={(locationId, _name, locationType) => {
                    updateForm({
                      routeLocationBId: locationId ?? '',
                      routeLocationBType: locationType ?? '',
                      freightLaneRouteId: '',
                    });
                    if (locationId && form.routeLocationAId) {
                      updateForm({
                        destinationCity: '',
                        destinationState: '',
                        destinationZipCode: '',
                        destinationCountry: '',
                        originLocationId: '',
                        originLocationType: '',
                      });
                    }
                  }}
                  isDisabled={isFormDisabled}
                />
              </Col>
            </div>

            <div className="border-t pt-4">
              <Col gap4>
                <div className="text-sm font-medium text-neutral/70">
                  {t.freightLanes.dialog.sections.destination}
                </div>
                <LocationPickerField
                  label="Origin Location"
                  value={form.originLocationId || null}
                  onChange={(locationId, _name, locationType) => {
                    updateForm({
                      originLocationId: locationId ?? '',
                      originLocationType: locationType ?? '',
                    });
                  }}
                  isRequired={!form.routeLocationAId && !form.routeLocationBId}
                  isDisabled={
                    isFormDisabled ||
                    (!!form.routeLocationAId && !!form.routeLocationBId)
                  }
                />
                <TextField
                  label={t.freightLanes.dialog.fields.destinationCity}
                  value={form.destinationCity}
                  onChange={(value: string) =>
                    updateForm({ destinationCity: value })
                  }
                  isDisabled={
                    isFormDisabled ||
                    (!!form.routeLocationAId && !!form.routeLocationBId)
                  }
                />

                <TextField
                  label={t.freightLanes.dialog.fields.destinationState}
                  value={form.destinationState}
                  onChange={(value: string) =>
                    updateForm({ destinationState: value })
                  }
                  isDisabled={
                    isFormDisabled ||
                    (!!form.routeLocationAId && !!form.routeLocationBId)
                  }
                />

                <TextField
                  label={t.freightLanes.dialog.fields.destinationZipCode}
                  value={form.destinationZipCode}
                  onChange={(value: string) =>
                    updateForm({ destinationZipCode: value })
                  }
                  isDisabled={
                    isFormDisabled ||
                    (!!form.routeLocationAId && !!form.routeLocationBId)
                  }
                />

                <TextField
                  label={t.freightLanes.dialog.fields.destinationCountry}
                  value={form.destinationCountry}
                  onChange={(value: string) =>
                    updateForm({ destinationCountry: value })
                  }
                  isDisabled={
                    isFormDisabled ||
                    (!!form.routeLocationAId && !!form.routeLocationBId)
                  }
                />
              </Col>
            </div>

            {isCreating && (
              <div className="border-t pt-4">
                <Col gap3>
                  <div className="text-sm font-medium text-neutral/70">
                    Initial Cost
                  </div>
                  <Row gap3>
                    <DatePicker
                      label="Effective Start"
                      value={costStartDate}
                      onChange={setCostStartDate}
                      className="flex-1"
                    />
                    <DatePicker
                      label="Effective End"
                      value={costEndDate}
                      onChange={setCostEndDate}
                      className="flex-1"
                    />
                  </Row>
                  <TextField
                    label="Rate Formula"
                    value={costRateFormula}
                    onChange={setCostRateFormula}
                  />
                  <TextField
                    label="Chargeback Formula"
                    value={costChargebackFormula}
                    onChange={setCostChargebackFormula}
                  />
                  <Row gap3>
                    <TextField
                      label="Fuel Surcharge Formula"
                      value={costFuelSurchargeFormula}
                      onChange={setCostFuelSurchargeFormula}
                      className="flex-1"
                    />
                    <Select
                      label="Fuel Surcharge Mode"
                      value={costFuelSurchargeMode || undefined}
                      onChange={(value) =>
                        setCostFuelSurchargeMode(value ?? '')
                      }
                      options={[
                        { key: 'PERCENTAGE', label: 'Percentage' },
                        { key: 'FLAT', label: 'Flat' },
                        { key: 'PER_MILE', label: 'Per Mile' },
                      ]}
                      className="flex-1"
                    />
                  </Row>
                  <Row gap3>
                    <TextField
                      label="Minimum Charge"
                      value={costMinimumCharge}
                      onChange={setCostMinimumCharge}
                      className="flex-1"
                    />
                    <TextField
                      label="Currency"
                      value={costCurrency}
                      onChange={setCostCurrency}
                      className="flex-1"
                    />
                  </Row>
                </Col>
              </div>
            )}

            <Checkbox
              isSelected={form.active}
              onChange={(value: boolean) => updateForm({ active: value })}
              isDisabled={isFormDisabled}
            >
              {t.freightLanes.dialog.fields.active}
            </Checkbox>

            <TextArea
              label={t.freightLanes.dialog.fields.notes}
              value={form.notes}
              onChange={(value: string) => updateForm({ notes: value })}
              isDisabled={isFormDisabled}
            />

            {freightLane && (
              <div className="border-t pt-4">
                <FreightLaneCostHistory freightLaneId={freightLane.id} />
              </div>
            )}
          </Col>
        </DialogMain>

        <DialogFooter>
          {isCreating ? (
            <>
              <Button variant="outline" onPress={handleClose}>
                {t.freightLanes.dialog.buttons.cancel}
              </Button>
              <Button
                color="primary"
                variant="solid"
                onPress={handleSave}
                isPending={isPending}
                isLoading={isPending}
              >
                {t.freightLanes.dialog.buttons.create}
              </Button>
            </>
          ) : isEditMode ? (
            <>
              <Button
                color="failure"
                variant="outline"
                onPress={handleDelete}
                isPending={isDeletePending}
              >
                {t.freightLanes.dialog.buttons.delete}
              </Button>
              <Button
                variant="outline"
                isPending={isPending}
                onPress={() => {
                  if (freightLane) {
                    updateForm({
                      carrierId: freightLane.carrierId,
                      transportationMode: 'TRUCK',
                      originLocationId: freightLane.originLocationId ?? '',
                      originLocationType: freightLane.originLocationType ?? '',
                      freightLaneRouteId: freightLane.freightLaneRouteId ?? '',
                      routeLocationAId: freightLane.routeLocationAId ?? '',
                      routeLocationBId: freightLane.routeLocationBId ?? '',
                      destinationCity: freightLane.destinationCity ?? '',
                      destinationState: freightLane.destinationState ?? '',
                      destinationZipCode: freightLane.destinationZipCode ?? '',
                      destinationCountry: freightLane.destinationCountry ?? '',
                      active: freightLane.active,
                      notes: freightLane.notes ?? '',
                    });
                  }
                  setIsEditMode(false);
                }}
              >
                {t.freightLanes.dialog.buttons.cancel}
              </Button>
              <Button
                color="primary"
                variant="solid"
                onPress={handleSave}
                isPending={isPending}
                isLoading={isPending}
              >
                {t.freightLanes.dialog.buttons.save}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onPress={handleClose}>
                {t.freightLanes.dialog.buttons.close}
              </Button>
              <Button
                color="primary"
                variant="solid"
                onPress={() => setIsEditMode(true)}
              >
                {t.freightLanes.dialog.title.edit}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
